import {
  tags,
  unwrapDeepRefs,
  type DeepMaybeRef,
  type MaybeRef,
} from "@/api/common";
import { useLocationSelector } from "@/composables/location";
import { endpoints } from "@/endpoints";
import useApiStore from "@/stores/api";
import { useQuery } from "@tanstack/vue-query";
import axios from "axios";
import { computed, unref } from "vue";
import { queryKeys } from "../queryKeys";
import type {
  EmployeeParams,
  EmployeeApiResponse,
  Employee,
  EmployeeData,
  EmployeesResponse,
  EmployeeResponse,
} from "./types";
import { ROLES, TINY_INT_AS_STRING } from "@/stores/constants";
import { parseToObject } from "@/utils/string";

export const mapEmployee = (employee: EmployeeApiResponse): Employee => {
  const formattedEmployee = {
    id: employee.pk,
    numericId: employee.id,
    department: employee.department && {
      id: employee.department.id ?? employee.department.pk,
      name: employee.department.name,
      userCount: employee.department.userCount,
    },
    email: employee.email,
    firstName: employee.first_name,
    lastName: employee.last_name,
    loading: employee.loading ?? false,
    locationId: Number(employee.location_id),
    phone: employee.phone,
    phoneCc: employee.phone_cc,
    photoKey: employee.photo_key,
    role: employee.role,
    noSync: employee.no_sync,
    emailCc: employee.email_cc,
    anonymizedAt: employee.anonymized_at,
    asEmployee: employee.as_employee,
    companyId: employee.company_id,
    createdAt: employee.created_at,
    customFields: employee.customFields,
    deletedAt: employee.deleted_at,
    departmentId: employee.department_id,
    directoryData: employee.directory_data ?? null,
    hasAuthenticated: employee.has_authenticated ?? false,
    isRemote: employee.is_remote ?? false,
    metadata: JSON.parse(employee.metadata) ?? {},
    newUser: employee.new_user,
    photos: employee.photos,
    updatedAt: employee.updated_at,
    settings: JSON.parse(employee.settings) ?? {},
    currentIn: employee.currentIn && {
      finishedAt: employee.currentIn.finished_at_tz,
      id: employee.currentIn.pk,
      locationId: Number(employee.currentIn.location_id),
      notes: employee.currentIn.notes,
      proximity: employee.currentIn.proximity,
      remote: employee.currentIn.remote === TINY_INT_AS_STRING.TRUE,
      reviewStatus: employee.currentIn.review_status,
      source: employee.currentIn.source,
      startedAt: employee.currentIn.started_at_tz,
      status: {
        ...employee.currentIn.status,
        enabled: employee.currentIn.status.enabled === TINY_INT_AS_STRING.TRUE,
      },
    },
    currentStatus: employee.currentStatus && {
      finishedAt: employee.currentStatus.finished_at_tz,
      id: employee.currentStatus.pk,
      locationId: employee.currentStatus.location_id,
      notes: employee.currentStatus.notes,
      proximity: employee.currentStatus.proximity,
      remote: employee.currentStatus.remote === TINY_INT_AS_STRING.TRUE,
      reviewStatus: employee.currentStatus.review_status,
      source: employee.currentStatus.source,
      startedAt: employee.currentStatus.started_at_tz,
      status: {
        ...employee.currentStatus.status,
        enabled:
          employee.currentStatus.status.enabled === TINY_INT_AS_STRING.TRUE,
      },
      reviewedBy: Number(employee.currentStatus.reviewed_by),
      reviewedAt: employee.currentStatus.reviewed_at,
    },
  } as Employee;

  if (employee?.location) {
    formattedEmployee.location = {
      id: employee?.location.pk,
      numericId: Number(employee?.location.id),
      name: employee?.location.name,
    };
  }

  return formattedEmployee;
};

export const useListEmployee = (filters?: DeepMaybeRef<EmployeeParams>) => {
  const apiStore = useApiStore();
  const { locationParams, isLocationsLoaded } = useLocationSelector();

  const filtersMerged = computed(() => ({
    ...locationParams.value,
    ...unwrapDeepRefs(filters),
  }));

  return useQuery({
    queryKey: queryKeys[tags.platform.employees].list(filtersMerged).queryKey,
    queryFn: async ({ signal }) => {
      const params: EmployeeParams = {
        ...unwrapDeepRefs(filtersMerged),
        expand: "counts",
      };

      const searchQuery = params?.["search[name]"];
      if (searchQuery) {
        params["search[name]"] = `%${(searchQuery ?? "")
          .toString()
          .toLowerCase()}%`;
      }

      const result = await axios.get<EmployeesResponse>(
        apiStore.getEndpoint("platform", endpoints.employees.list),
        { params, signal }
      );
      return result.data;
    },
    select: (data): EmployeeData => ({
      counts: data.counts,
      list: data.list.map((employeeData) => mapEmployee(employeeData)),
    }),
    keepPreviousData: true,
    enabled: isLocationsLoaded,
  });
};

export const useGetAdminRolesEnabled = () => {
  const apiStore = useApiStore();

  const filtersMerged = computed(() => ({
    "search0[role.role_id]": ROLES.COMPANY_ADMIN_ID,
    limit: -1,
  }));

  return useQuery({
    queryKey: queryKeys[tags.platform.employees].list(filtersMerged).queryKey,
    queryFn: async () => {
      const result = await axios.get<EmployeesResponse>(
        apiStore.getEndpoint("platform", endpoints.employees.list),
        {
          params: filtersMerged.value,
        }
      );
      return result.data;
    },
    select: (data) =>
      data.list
        .map((employee) => ({
          ...employee,
          settings: parseToObject(employee.settings),
        }))
        .some(
          (employee) => employee.settings.lg_admin_location_group_pks.length
        ),
  });
};

export const useGetEmployee = (id: MaybeRef<string | undefined>) => {
  const apiStore = useApiStore();
  const enabled = computed(() => Boolean(unref(id)));

  return useQuery({
    queryKey: queryKeys[tags.platform.employees].byId(id).queryKey,
    queryFn: async () => {
      const result = await axios.get<{
        user: EmployeeApiResponse;
      }>(
        apiStore.getEndpoint(
          "platform",
          endpoints.employees.get(String(unref(id)))
        )
      );
      return result.data;
    },
    select: (data) => mapEmployee(data.user),
    enabled,
    refetchOnWindowFocus: false,
  });
};

export const useGetEmployeeById = (
  employeeId: MaybeRef<string | undefined>
) => {
  const apiStore = useApiStore();

  return useQuery({
    queryKey: queryKeys[tags.platform.employees].byId(employeeId).queryKey,
    queryFn: async ({ signal }) => {
      const result = await axios.get<EmployeeResponse>(
        apiStore.getEndpoint(
          "platform",
          endpoints.user(String(unref(employeeId)))
        ),
        { signal }
      );
      return result.data;
    },
    select: (data) => mapEmployee(data.user),
    enabled: computed(() => Boolean(unref(employeeId))),
  });
};
